/**
 * Media Devices store
 *
 * Holds a list of all current audio input, audio output and video input devices, the preferred devices and the currently selected devices,
 * and announces when a device has been removed or added.
 *
 */

import { computed, watch, ref } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'

import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import _i18n from '@/utilities/I18n'
import { remapPreferredDeviceIds } from '@/utilities/mediaDeviceMatching'

import { MediaDeviceType, type MediaDevice, type MediaDeviceSettingsItem } from './MediaDevices.types'

/** Matches raw labels of known virtual audio/video devices that must never become a preferred device */
const VIRTUAL_DEVICE_PATTERN = /virtual|virtuel|vb-audio|vb-cable|voicemeeter|steinberg|blackhole|soundflower|aggregate|multi-output|loopback|cable/i

export const useMediaDevicesStore = defineStore('mediaDevices', () => {
	logger.debug('MediaDevices: Initializing store')

	const ss = useSettingsStore()
	const i18n = _i18n.global

	const devices = ref<MediaDevice[]>([])
	const hasAudioPermission = ref(false)
	const hasVideoPermission = ref(false)
	const _ready = ref(false)

	/** Is media devices is initialized and ready to use */
	const isReady = computed(() => _ready.value)

	globalEvents.on('StatusRequest', () => {
		globalEvents.emit('StatusReport', { key: 'mediaDevices', value: _ready.value })
		globalEvents.emit('StatusReport', { key: 'mediaDevicesList', value: devices.value })
	})

	/** Initialize */
	async function init() {
		logger.info('MediaDevices: Initializing')
		const permission1 = await requestMedia(true, true)
		if (permission1) {
			logger.info('MediaDevices: Got access to audio and video')
			hasAudioPermission.value = true
			hasVideoPermission.value = true
			stopMedia(permission1)
		} else {
			const permission2 = await requestMedia(true, false)
			if (permission2) {
				logger.info('MediaDevices: Got access to audio only')
				hasAudioPermission.value = true
				stopMedia(permission2)
			} else {
				logger.warning('MediaDevices: Failed to get access to audio and video')
			}
		}

		logger.debug(`MediaDevices: Preferred devices: audioInputs: ${ss.settings.media.preferredAudioInputs[0] ?? 'n/a'} (${ss.settings.media.preferredAudioInputs.length})`)
		logger.debug(`MediaDevices: Preferred devices: audioOutputs: ${ss.settings.media.preferredAudioOutputs[0] ?? 'n/a'} (${ss.settings.media.preferredAudioOutputs.length})`)
		logger.debug(`MediaDevices: Preferred devices: videoInputs: ${ss.settings.media.preferredVideoInputs[0] ?? 'n/a'} (${ss.settings.media.preferredVideoInputs.length})`)
		logger.debug(`MediaDevices: Preferred devices: ringerOutputs: ${ss.settings.media.preferredRingerOutputs[0] ?? 'n/a'} (${ss.settings.media.preferredRingerOutputs.length})`)

		navigator.mediaDevices.ondevicechange = () => { buildDeviceList() }
		await buildDeviceList(true)
		_ready.value = true
	}

	/** Returns when all data has been loaded and the module is ready to be used */
	function ready() {
		if (_ready.value) { return Promise.resolve() }
		return new Promise(resolve => {
			const unwatch = watch(_ready, value => {
				if (value) {
					unwatch()
					resolve(undefined)
				}
			})
		})
	}

	/**
	 * Request media streams and access
	 * @param audio True for any audio device, or the deviceId of a specific audio input device
	 * @param video true for any video device, or the deviceId of a specific video input device
	 * @param width Desired video width
	 * @param height Desired video height
	 */
	async function requestMedia(audio: boolean | string, video: boolean | string, width?: number, height?: number) {
		// Request permission to access audio/video devices (getUserMedia) by initializing them
		const config: MediaStreamConstraints = { audio: false, video: false }

		if (typeof audio === 'boolean') { config.audio = audio }
		if (typeof video === 'boolean') { config.video = video }
		// Electron ignores bare deviceId constraints (they are only "ideal" hints), so require the device explicitly
		if (typeof audio === 'string') { config.audio = { deviceId: { exact: audio } } }
		if (typeof video === 'string') { config.video = { deviceId: { exact: video } } }
		if (width && height) {
			if (typeof config.video === 'boolean') {
				config.video = { width: { ideal: width }, height: { ideal: height } }
			} else if (typeof config.video === 'object') {
				config.video.width = { ideal: width }
				config.video.height = { ideal: height }
			}
		}

		try {
			return await navigator.mediaDevices.getUserMedia(config)
		}
		catch (err) {
			logger.error(`MediaDevices: RequestMedia failed: ${(err as Error).message}`)
			return null
		}
	}

	/**
	 * Stop all tracks on a media stream
	 * @param mediaStream Media stream
	 */
	function stopMedia(mediaStream: MediaStream) {
		try {
			const tracks = mediaStream.getTracks()

			for (const track of tracks) {
				if (track) { track.stop() }
			}
		} catch (_) { /* Ignore */ }
	}

	/**
	 * Build list of devices
	 * @param initial True on initial run, false when invoked because of device change
	 * @private
	 */
	async function buildDeviceList(initial = false) {
		logger.info('MediaDevices: Building list of devices')

		try {
			// Build list of devices as it looks like now
			const rawDevices = await navigator.mediaDevices.enumerateDevices() // deviceId, kind, label, groupId
			const foundDevices: MediaDevice[] = []

			for (const device of rawDevices) {
				if (device.deviceId === 'communications') { continue }

				const hasProductId = RegExp(/\([a-fA-F0-9]{4}:[a-fA-F0-9]{4}\)/).exec(device.label)
				const productId = hasProductId ? hasProductId[0].slice(1, -1) : undefined
				const foundDevice: MediaDevice = {
					deviceId: device.deviceId,
					kind: device.kind as MediaDeviceType,
					label: device.deviceId === 'default' ? 'Standard enhed' : cleanDeviceName(device.label),
					groupId: device.groupId,
					productId: productId ?? '',
					rawLabel: device.label
				}

				foundDevices.push(foundDevice)
				logger.debug(`MediaDevices: Found device: ID=${foundDevice.deviceId} KIND=${foundDevice.kind} LABEL=${foundDevice.label} RAW=${foundDevice.rawLabel}`)
			}

			if (!initial) {
				// Device(s) has changed - find which one
				const removed = devices.value.filter(currentDevice => foundDevices.find(foundDevice => foundDevice.deviceId === currentDevice.deviceId) == null)
				const added = foundDevices.filter(foundDevice => devices.value.find(currentDevice => currentDevice.deviceId === foundDevice.deviceId) == null)

				devices.value = foundDevices

				// A device re-enumerated with a new deviceId (e.g. a headset switching USB audio
				// profile) would otherwise invalidate the user's selection and make it fall through
				// to a previously-preferred device. Pin each preferred selection to the same physical
				// device so the choice follows it to its new id.
				const media = ss.settings.media
				const pin = (label: string, list: string[]) => {
					const remapped = remapPreferredDeviceIds(list, removed, added)
					const changed = remapped.some((id, index) => id !== list[index])

					if (changed) {
						logger.info(`MediaDevices: Re-pinned ${label} to re-enumerated device(s): [${list.join(', ')}] -> [${remapped.join(', ')}]`)
					}

					return changed ? remapped : list
				}

				media.preferredAudioInputs = pin('microphone', media.preferredAudioInputs)
				media.preferredAudioOutputs = pin('speaker', media.preferredAudioOutputs)
				media.preferredVideoInputs = pin('camera', media.preferredVideoInputs)
				media.preferredRingerOutputs = pin('ringer', media.preferredRingerOutputs)

				for (const device of removed) {
					logger.debug(`MediaDevices: Device removed: ${device.deviceId} - ${device.label} - ${device.kind} - ${device.productId}`)
					globalEvents.emit('MediaDeviceChange', { event: 'removed', ...device })
				}

				for (const device of added) {
					logger.debug(`MediaDevices: Device added: ${device.deviceId} - ${device.label} - ${device.kind} - ${device.productId}`)
					globalEvents.emit('MediaDeviceChange', { event: 'added', ...device })
				}
			} else {
				devices.value = foundDevices
				globalEvents.emit('MediaDeviceSelected', { type: 'audioinput' })
			}
			logger.debug(`MediaDevices: Build list of devices with ${devices.value.length} devices`)
		}
		catch (err) {
			logger.warning(`MediaDevices: Build list failed: ${(err as Error).message}`)
		}
	}

	// #region Public

	/**
	 * True if a device must never be stored as a preferred device (the system default or a virtual device)
	 * @param device Device to check
	 */
	function isExcludedDevice(device: MediaDevice) {
		if (device.deviceId === 'default' || device.deviceId === 'communications') { return true }
		return VIRTUAL_DEVICE_PATTERN.test(device.rawLabel)
	}

	/**
	 * True if a device id refers to an excluded (default/virtual) device
	 * @param id Id of device
	 */
	function isExcludedDeviceId(id: string) {
		if (id === 'default' || id === 'communications') { return true }

		const device = devices.value.find(device => device.deviceId === id)

		return device ? isExcludedDevice(device) : false
	}

	/**
	 * Get the name/label of a device
	 * @param id Id of device
	 * @returns Name of device, null if not found
	 */
	function getDeviceName(id: string) {
		const device = devices.value.find(device => device.deviceId === id)

		return device?.label ?? null
	}

	/**
	 * Check if a device is available and optionally of the right type
	 * @param id Id of device
	 * @param type Optional type of device: audioinput, audiooutput, videoinput
	 */
	function isDeviceValid(id: string, type?: MediaDeviceType) {
		if (type) {
			return devices.value.some(device => device.deviceId === id && device.kind === type)
		} else {
			return devices.value.some(device => device.deviceId == id)
		}
	}

	/** Id of selected microphone, fallback to 'default' */
	const microphoneDeviceId = computed(() => {
		for (const deviceId of ss.settings.media.preferredAudioInputs) {
			if (isDeviceValid(deviceId)) { return deviceId }
		}
		return 'default'
	})

	/** The selected microphone device */
	const microphoneDevice = computed(() => {
		const id = microphoneDeviceId.value

		return devices.value.find(device => device.deviceId === id && device.kind === MediaDeviceType.audioinput)
	})

	/** List of microphone devices - for settings */
	const audioInDevices = computed(() => {
		const selected = microphoneDeviceId.value

		return devices.value
			.filter(device => device.kind === MediaDeviceType.audioinput)
			.sort((a, b) => a.label.localeCompare(b.label))
			.map(o => { return {
				value: o.deviceId,
				label: o.label,
				group: o.groupId,
				selected: o.deviceId === selected,
				disabled: isExcludedDevice(o)
			} }) as MediaDeviceSettingsItem[]
	})

	/** Id of selected speaker, fallback to 'default' */
	const speakerDeviceId = computed(() => {
		for (const deviceId of ss.settings.media.preferredAudioOutputs) {
			if (isDeviceValid(deviceId)) { return deviceId }
		}
		return 'default'
	})

	/** The selected speaker device */
	const speakerDevice = computed(() => {
		const id = speakerDeviceId.value

		return devices.value.find(device => device.deviceId === id && device.kind === MediaDeviceType.audiooutput)
	})

	/** List of speaker devices - for settings */
	const audioOutDevices = computed(() => {
		const selected = speakerDeviceId.value

		return devices.value
			.filter(device => device.kind === MediaDeviceType.audiooutput)
			.sort((a, b) => a.label.localeCompare(b.label))
			.map(device => { return {
				value: device.deviceId,
				label: device.label,
				group: device.groupId,
				selected: device.deviceId === selected,
				disabled: isExcludedDevice(device)
			} }) as MediaDeviceSettingsItem[]
	})

	/** Id of the selected ringer device - fallback to 'default' */
	const ringerDeviceId = computed(() => {
		for (const deviceId of ss.settings.media.preferredRingerOutputs) {
			if (isDeviceValid(deviceId)) { return deviceId }
		}
		return 'default'
	})

	/** The selected speaker device */
	const ringerDevice = computed(() => {
		const id = ringerDeviceId.value

		return devices.value.find(device => device.deviceId === id && device.kind === MediaDeviceType.audiooutput)
	})

	/** Id of selected video input, fallback to 'default' */
	const videoDeviceId = computed(() => {
		for (const deviceId of ss.settings.media.preferredVideoInputs) {
			if (isDeviceValid(deviceId)) { return deviceId }
		}
		return null
	})

	/** The selected speaker device */
	const videoDevice = computed(() => {
		const id = videoDeviceId.value

		return devices.value.find(device => device.deviceId === id && device.kind === MediaDeviceType.videoinput)
	})

	/** list of video input devices - for settings */
	const videoInDevices = computed(() => {
		const selected = videoDeviceId.value
		const list: MediaDeviceSettingsItem[] = devices.value
			.filter(device => device.kind === MediaDeviceType.videoinput)
			.sort((a, b) => a.label.localeCompare(b.label))
			.map(device => { return {
				value: device.deviceId,
				label: device.label,
				group: device.groupId,
				selected: device.deviceId === selected,
				disabled: isExcludedDevice(device)
			} })

		list.unshift({ value: 'NONE', label: 'Benyt ikke', group: undefined, selected: selected == null, disabled: false })
		return list
	})

	const audioInputs = computed(() => { return devices.value.filter(device => device.kind === MediaDeviceType.audioinput) })
	const audioOutputs = computed(() => { return devices.value.filter(device => device.kind === MediaDeviceType.audiooutput) })
	const videoInputs = computed(() => { return devices.value.filter(device => device.kind === MediaDeviceType.videoinput) })

	/** Returns list of device groups for settings */
	const deviceGroups = computed(() => {
		const list: Record<string, { groupId: string, name: string, devices: MediaDevice[] }> = {}

		for (const dev of devices.value) {
			if (dev.label === 'Standard enhed') { continue }
			if (!list[dev.groupId]) {
				list[dev.groupId] = { groupId: dev.groupId, name: dev.label, devices: [] }
			}
			list[dev.groupId]!.devices.push(dev) // eslint-disable-line @typescript-eslint/no-non-null-assertion
		}

		return Object.values(list)
	})

	/** Returns the ringer volume */
	const ringerVolume = computed(() => ss.settings.media.ringerVolume)

	function setMicrophoneDeviceId(id?: string) {
		if (id) {
			if (isExcludedDeviceId(id)) {
				logger.info(`MediaDevices: Ignoring microphone selection of excluded (default/virtual) device id ${id}`)
				return
			}
			logger.info(`MediaDevices: Selected microphone with device id ${id}`)
			ss.settings.media.preferredAudioInputs = ss.settings.media.preferredAudioInputs.filter(deviceId => deviceId !== id) // Ensure we only have the device once
			ss.settings.media.preferredAudioInputs.unshift(id) // add to top of list
			if (ss.settings.media.preferredAudioInputs.length > 10) { ss.settings.media.preferredAudioInputs.pop() }
			globalEvents.emit('MediaDeviceSelected', { type: 'audioinput' })
		}
	}

	function setSpeakerDeviceId(id?: string) {
		if (id) {
			if (isExcludedDeviceId(id)) {
				logger.info(`MediaDevices: Ignoring speaker selection of excluded (default/virtual) device id ${id}`)
				return
			}
			logger.info(`MediaDevices: Selected speaker with device id ${id}`)
			ss.settings.media.preferredAudioOutputs = ss.settings.media.preferredAudioOutputs.filter(deviceId => deviceId !== id) // Ensure we only have the device once
			ss.settings.media.preferredAudioOutputs.unshift(id) // add to top of list
			if (ss.settings.media.preferredAudioOutputs.length > 10) { ss.settings.media.preferredAudioOutputs.pop() }
			globalEvents.emit('MediaDeviceSelected', { type: 'audiooutput' })
		}
	}

	function setVideoDeviceId(id: string) {
		if (id && id !== 'NONE' && isExcludedDeviceId(id)) {
			logger.info(`MediaDevices: Ignoring video selection of excluded (virtual) device id ${id}`)
			return
		}
		logger.info(`MediaDevices: Selected video with device id ${id}`)
		if (id && id !== 'NONE') {
			ss.settings.media.preferredVideoInputs = ss.settings.media.preferredVideoInputs.filter(deviceId => deviceId !== id) // Ensure we only have the device once
			ss.settings.media.preferredVideoInputs.unshift(id) // add to top of list
		} else {
			ss.settings.media.preferredVideoInputs = []
		}
		if (ss.settings.media.preferredVideoInputs.length > 10) { ss.settings.media.preferredVideoInputs.pop() }
		globalEvents.emit('MediaDeviceSelected', { type: 'videoinput' })
	}

	function setRingerDeviceId(id: string) {
		logger.info(`MediaDevices: Selected ringer with device id ${id}`)
		if (id) {
			ss.settings.media.preferredRingerOutputs = ss.settings.media.preferredRingerOutputs.filter(deviceId => deviceId !== id) // Ensure we only have the device once
			ss.settings.media.preferredRingerOutputs.unshift(id) // add to top of list
		}
		if (ss.settings.media.preferredRingerOutputs.length > 10) { ss.settings.media.preferredRingerOutputs.pop() }
		globalEvents.emit('MediaDeviceSelected', { type: 'ringeroutput' })
	}

	function setRingerVolume(volume: number) {
		logger.info(`MediaDevices: Set ringer volume to ${volume}`)
		if (volume >= 0 && volume <= 1) { ss.settings.media.ringerVolume = volume }
		globalEvents.emit('MediaDeviceSelected', { type: 'ringervolume' })
	}

	/**
	 * Reset the preferred microphone, speaker and camera lists so each keeps only the currently selected device.
	 * If a list currently resolves to the system default (no valid real device), it is emptied.
	 * The ringer preference is left untouched.
	 */
	function resetPreferredDevices() {
		logger.info('MediaDevices: Resetting preferred devices to current selection')

		const micId = microphoneDeviceId.value
		const speakerId = speakerDeviceId.value
		const videoId = videoDeviceId.value

		ss.settings.media.preferredAudioInputs = isExcludedDeviceId(micId) ? [] : [micId]
		ss.settings.media.preferredAudioOutputs = isExcludedDeviceId(speakerId) ? [] : [speakerId]
		ss.settings.media.preferredVideoInputs = (videoId && !isExcludedDeviceId(videoId)) ? [videoId] : []

		globalEvents.emit('MediaDeviceSelected', { type: 'audioinput' })
		globalEvents.emit('MediaDeviceSelected', { type: 'audiooutput' })
		globalEvents.emit('MediaDeviceSelected', { type: 'videoinput' })
	}

	// #endregion

	function cleanDeviceName(a: string) {
		a = a.replace('Microphone', '')
			.replace('Mikrofonmatrice', '')
			.replace('Integrated', i18n.t('mediaDevices.builtin'))
			.replace('Camera', '')
			.replace('Speakers', '')
			.replace('Speaker', '')
			.replace('Headset Earphone', '')
			.replace('Headset Speaker', '')
			.replace('Headset', '')
			.replace('Communications', i18n.t('mediaDevices.communication'))
			.replace('Virtual', i18n.t('mediaDevices.virtual'))
			.replace('Internal', i18n.t('mediaDevices.builtin'))
			.replace('(Built-in)', '')
			.replace('Default', i18n.t('mediaDevices.default'))

		if (!a.startsWith('Mikrofon i')) { a = a.replace('Mikrofon', '') }

		const m1 = RegExp(/ \([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)$/).exec(a)
		if (m1) { a = a.substring(0, m1.index) }
		a = a.replace(/ {2}/g, ' ').trim()

		if (a.startsWith('(') && a.endsWith(')')) { a = a.slice(0, -1) }
		if (a.startsWith('(')) { a = a.substring(1) }
		if (RegExp(/^\d- \w/).exec(a)) { a = a.substring(3) }
		return a
	}

	logger.debug('MediaDevices: Store created')

	return { devices, hasAudioPermission, hasVideoPermission, isReady,
		microphoneDeviceId, microphoneDevice, audioInDevices, speakerDeviceId, speakerDevice, audioOutDevices, videoDeviceId, videoDevice, videoInDevices,
		ringerDeviceId, ringerDevice, ringerVolume, audioInputs, audioOutputs, videoInputs, deviceGroups,
		init, ready, getDeviceName, setMicrophoneDeviceId, setSpeakerDeviceId, setVideoDeviceId, setRingerDeviceId, setRingerVolume, resetPreferredDevices
	}
})
